import React, { useState, useEffect } from 'react';
import {
  withStyles,
  Modal,
} from '@material-ui/core';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  deleteHourMachineItem,
  setHourMachineValues,
  updateHourMachineList,
  restoreOriginalHourMachine,
  createHourMachineItem,
  searchHourMachineList,
} from '~/store/dailyparts/actions';
import { getCodeTypes } from '~/store/manager/actions';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import EditHistoryModal from '~/components/EditHistoryModal';
import PF2MDataTable from '~/components/PF2MDataTable';
import {
  formatDate, formatTime, newMomentDate, parseDate, subtractDaysFromNow,
} from '~/utils/moment';
import EditHistoryButton from '~/components/EditHistoryButton';
import usePlatformConfiguration, { usePlatformConfigurationUserAccessLevel } from '~/services/platformConfiguration';
import { usePagination } from '../hooks';

const HourMachineList = () => {
  const { t: translate } = useTranslation();

  const hourMachineList = useSelector(state => state.dailyParts.hourMachineList);
  const filters = useSelector(state => state.dailyParts.filters);
  const rawCodeTypes = useSelector(state => state.manager.codeTypes, shallowEqual);
  const equipmentTypes = useSelector(state => state.manager.equipmentTypes);
  const equipmentsGroups = useSelector(state => state.manager.equipmentsGroups);
  const equipments = useSelector(state => state.manager.equipments);
  const operatorsGroups = useSelector(state => state.manager.operatorsGroups);
  const operators = useSelector(state => state.manager.operators);
  const codeGroups = useSelector(state => state.manager.codeGroups);
  const codes = useSelector(state => state.manager.codes);

  const userAccessLevel = useSelector(state => state.auth.user.level);
  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(userAccessLevel);
  const closures = useSelector(state => state.manager.closures);
  const { value: enableClosure } = usePlatformConfiguration('enable_closure');

  const originalHourMachineList = useSelector(
    state => state.dailyParts.originalHourMachineList,
  );
  const hourMachinePagination = useSelector(
    state => state.dailyParts.pagination.hour_machinePagination,
  );

  const [openEditHistoryModal, setOpenEditHistoryModal] = useState(false);
  const [selectedEditHistoryData, setSelectedEditHistoryData] = useState(null);

  const dispatch = useDispatch();

  const codeTypes = rawCodeTypes.map(e => ({ ...e, name: translate(`common:${e.name}`) }));

  useEffect(() => {
    async function fetchCodeTypes() {
      dispatch(getCodeTypes());
    }

    if (codeTypes.length <= 0) {
      fetchCodeTypes();
    }
  });

  const isDateEditable = (rowData, key) => {
    let userLimitEdit = subtractDaysFromNow(userLimitEditConfig);

    if (enableClosure && closures?.length > 0) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closures[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimitEditConfig);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimitEditConfig
        ? lockEditConfig
        : lockEditLastClosure.add(1, 'day');
    }

    if (rowData[key] < userLimitEdit) {
      return { isValid: false, helperText: translate('validation:InvalidDate') };
    }

    return { isValid: true };
  };

  const validateFuelLevel = (rowData) => {
    const fuelLevel = rowData.fuel_level;
    if (Number.isNaN(Number(fuelLevel)) || fuelLevel < 1 || fuelLevel > 100) {
      return { isValid: false, helperText: translate('validation:FuelLevelMustBeBetween') };
    }

    return { isValid: true };
  };

  const columns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      title: translate('common:Date'),
      field: 'hour_machine_date',
      customType: 'date',
      validate: rowData => isDateEditable(rowData, 'hour_machine_date'),
    },
    {
      title: translate('common:Time'),
      field: 'hour_machine_time',
      customType: 'time',
    },
    {
      title: translate('common:CodeType'),
      field: 'code_type',
      lookup: codeTypes,
      customType: 'filterLookup',
    },
    {
      title: translate('common:CodeGroup'),
      field: 'code_group',
      lookup: codeGroups,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.code_type) === String(rowData.code_type)
      && String(lookup.id_equip) === String(rowData.equip_type),
    },
    {
      title: translate('common:Code'),
      field: 'codes_id',
      customType: 'filterLookup',
      lookup: codes,
      render: rowData => codes.find(
        e => e.id_group === rowData.code_group && e.id === rowData.codes_id,
      )?.name || null,
      lookupFilter: (codeOption, row) => {
        if (String(row.code_group) === '0') {
          return (
            String(row.code_type) === String(codeOption.code_type)
            && String(row.equip_type) === String(codeOption.id_equip)
          );
        }
        return (
          String(codeOption.id_group) === String(row.code_group)
          && String(row.equip_type) === String(codeOption.id_equip)
        );
      },
      tooltip: true,
    },
    {
      title: translate('common:OperatorGroup'),
      field: 'operator_group',
      lookup: operatorsGroups.filter(o => o.name),
      customType: 'filterLookup',
    },
    {
      title: translate('common:Operator'),
      field: 'operator_id',
      lookupKey: 'id_operator',
      lookup: operators.filter(o => o.name),
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_group) === String(rowData.operator_group),
    },
    {
      title: translate('common:EquipmentType'),
      field: 'equip_type',
      lookup: equipmentTypes,
      customType: 'filterLookup',
      editable: 'never',
    },
    {
      title: translate('common:EquipmentGroup'),
      field: 'equip_group',
      lookup: equipmentsGroups,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_equipament)
        === String(rowData.equip_type),
    },
    {
      title: translate('common:Equipment'),
      field: 'equip_id',
      lookup: equipments,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => String(lookup.id_group)
        === String(rowData.equip_group)
        || String(rowData.id_equip) === '0',
    },
    {
      title: translate('common:Condition'),
      field: 'condition',
      lookup: [
        { id: 0, name: translate('common:Exit') },
        { id: 1, name: translate('common:Entry') },
        { id: 2, name: translate('common:LoginLogout') },
      ],
      customType: 'filterLookup',
    },
    {
      title: translate('common:FuelLevel'),
      field: 'fuel_level',
      validate: rowData => validateFuelLevel(rowData),
    },
    {
      title: `${translate('common:Hourmeter')} 1`,
      field: 'hourmeter_1',
      customType: 'number',
    },
    {
      title: `${translate('common:Hourmeter')} 2`,
      field: 'hourmeter_2',
      customType: 'number',
    },
    {
      title: `${translate('common:Hourmeter')} 3`,
      field: 'hourmeter_3',
      customType: 'number',
    },
    {
      title: `${translate('common:Hourmeter')} 4`,
      field: 'hourmeter_4',
      customType: 'number',
    },
    {
      title: `${translate('common:Hourmeter')} 5`,
      field: 'hourmeter_5',
      customType: 'number',
    },
    {
      title: `${translate('common:Hourmeter')} 6`,
      field: 'hourmeter_6',
      customType: 'number',
    },
    {
      title: `${translate('common:Hourmeter')} 7`,
      field: 'hourmeter_7',
      customType: 'number',
    },
    {
      title: `${translate('common:Hourmeter')} 8`,
      field: 'hourmeter_8',
      customType: 'number',
    },
    {
      title: `${translate('common:Hourmeter')} 9`,
      field: 'hourmeter_9',
      customType: 'number',
    },
    {
      title: `${translate('common:Hourmeter')} 10`,
      field: 'hourmeter_10',
      customType: 'number',
    },
  ];

  const customEditHistoryColumns = [
    {
      title: translate('common:Date'),
      field: 'hour_machine_date',
      customType: 'date',
    },
    {
      title: translate('common:Time'),
      field: 'hour_machine_time',
      customType: 'time',
    },
    {
      title: translate('common:CodeType'),
      field: 'code_type',
      lookup: codeTypes,
    },
    {
      title: translate('common:CodeGroup'),
      field: 'code_group',
      lookup: codeGroups,
    },
    {
      title: translate('common:Code'),
      field: 'codes_id',
      lookup: codes,
    },
    {
      title: translate('common:OperatorGroup'),
      field: 'operator_group',
      lookup: operatorsGroups.filter(o => o.name),
    },
    {
      title: translate('common:Operator'),
      field: 'operator_id',
      lookupKey: 'id_operator',
      lookup: operators.filter(o => o.name),
    },
    {
      title: translate('common:EquipmentType'),
      field: 'equip_type',
      lookup: equipmentTypes,
    },
    {
      title: translate('common:EquipmentGroup'),
      field: 'equip_group',
      lookup: equipmentsGroups,
    },
    {
      title: translate('common:Equipment'),
      field: 'equip_id',
      lookup: equipments,
    },
    {
      title: translate('common:Condition'),
      field: 'condition',
      lookup: [
        { value: 0, label: translate('common:Exit') },
        { value: 1, label: translate('common:Entry') },
        { value: 2, label: translate('common:LoginLogout') },
      ],
    },
  ];

  const filterToFormFieldMap = {
    codeType: 'code_type',
    codeGroup: 'code_group',
    code: 'codes_id',
    operatorGroup: 'operator_group',
    operator: 'operator_id',
    equipmentGroup: 'equip_group',
    equipment: 'equip_id',
    equipmentType: 'equip_type',
    turn: 'turn_id',
  };
  const convertIfNeeded = (value, key) => {
    if (key === 'codeType' || key === 'codeGroup') {
      return Number(value);
    }
    return value;
  };

  const mappedFilterData = Object.entries(filterToFormFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      const filterValue = filters[filterKey];

      if (filterValue !== undefined && filterValue !== 0) {
        if (Array.isArray(filterValue && filterValue.length === 1)) {
          const [firstValue] = filterValue;
          if (firstValue !== undefined) {
            acc[formField] = convertIfNeeded(firstValue, filterKey);
          }
        } else {
          acc[formField] = convertIfNeeded(filterValue, filterKey);
        }
      }
      return acc;
    },
    {},
  );

  // só deve conseguir salvar todas as alterações caso todos os campos
  // obrigatórios das linhas alteradas sejam preenchidos.
  const disableSaveAllChangesButton = hourMachineList?.length ? hourMachineList
    .filter(row => row.touched)
    .flatMap(row => (
      Object.entries(row).map(([key, value]) => {
        if (key.startsWith('end_') || key.startsWith('start_')) return false;
        return columns.find(col => col.field === key)?.required && value === '';
      })
    ))
    .includes(true) : false;

  const initialFormData = {
    ...filters,
    ...mappedFilterData,
    startDate: formatDate(newMomentDate(), true),
    hour_machine_date: formatDate(newMomentDate(), true),
    hour_machine_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    team_id: '',
    hourmeter_1: '',
    hourmeter_2: '',
    hourmeter_3: '',
    hourmeter_4: '',
    hourmeter_5: '',
    hourmeter_6: '',
    hourmeter_7: '',
    hourmeter_8: '',
    hourmeter_9: '',
    hourmeter_10: '',
  };

  const removeUnnecessaryFields = (data) => {
    const result = { ...data };

    const fieldsToRemove = [
      'startDate',
      'endDate',
      'turn',
      'code',
      'equipmentType',
      'operatorGroup',
      'operator',
      'material',
      'startOrigin',
      'endOrigin',
      'startSubOrigin',
      'endSubOrigin',
      'codeType',
      'codeGroup',
      'searchDailyParts',
      'hideInterruptedOrDeleted',
      'showOnlyReservedEquips',
      'equipment',
      'equipmentGroup',
      'touched',
      'handlingType',
    ];

    fieldsToRemove.forEach((field) => {
      delete result[field];
    });

    return result;
  };

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'hour_machine',
    pagination: hourMachinePagination,
    searchAction: searchHourMachineList,
  });

  const { page = 0, total = 0, loadingMore = false } = hourMachinePagination || { };

  return (
    <>
      <PF2MDataTable
        title={translate('common:Cycles')}
        columns={applyDefaultSort(columns)}
        data={hourMachineList}
        initialFormData={initialFormData}
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showTitle: true,
          selection: true,
          search: false,
          cacheKey: 'hour_machine_list',
          maxBodyHeight: 'calc(100vh - 290px)',
          minBodyHeight: 'calc(100vh - 290px)',
          disableSaveButton: disableSaveAllChangesButton,
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        onAdd={newItem => dispatch(createHourMachineItem(newItem))}
        onDiscard={() => dispatch(restoreOriginalHourMachine())}
        onApply={() => {
          const parsedData = hourMachineList
            .filter(item => item.update_status)
            .map(removeUnnecessaryFields);

          const actionData = {
            data: parsedData,
            parameters: filters,
            saveLog: true,
            ogData: originalHourMachineList,
          };
          dispatch(updateHourMachineList(actionData));
        }}
        onChange={(newData) => {
          newData.forEach(({ ...row }) => {
            dispatch(setHourMachineValues(row, row));
          });
        }}
        onDelete={(newData) => {
          newData.forEach((e) => {
            dispatch(deleteHourMachineItem(e));
          });
        }}
      />
      <Modal
        open={openEditHistoryModal}
        onClose={() => setOpenEditHistoryModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditHistoryModal
          listName="hourMachine"
          referenceColumns={columns}
          customEditHistoryColumns={customEditHistoryColumns}
          selectedEditHistoryData={selectedEditHistoryData}
          handleClose={() => setOpenEditHistoryModal(false)}
        />
      </Modal>
    </>
  );
};

HourMachineList.propTypes = {};

HourMachineList.defaultProps = {};

export default withStyles(styles)(HourMachineList);
